export const ROUTES = {
  // Base app routes
  HOME: '/',
  /** Canonical marketplace URL — hosts both Suites and Agents tabs. */
  MARKETPLACE: '/marketplace',
  AGENTS_DETAILS: (agentKey: string) => `/agents/${agentKey}`,
  SUITE_DETAILS: (suiteKey: string) => `/suites/${suiteKey}`,
  SEE_IT_RUN: '/see-it-run',
  CONTACT: '/contact',
  PRICING: '/pricing',
  MEET_LIVE_AI: '/meet-live-ai',
  PRICING_SUITES: '/pricing/suites',
  PRICING_AGENTS: '/pricing/agents',
  INTEGRATIONS: '/integrations',
  INTEGRATIONS_ALL: '/integrations/all',
  BLOG: '/blog',
  BLOG_LEDGER: '/blog/the-ledger',
  BLOG_INFERENCE: '/blog/the-inference',
  BLOG_DETAILS: (
    postSlug: string,
    readableSlug?: string,
    variant?: 'ledger' | 'inference'
  ) => {
    const articleSlug = readableSlug ? `${postSlug}-${readableSlug}` : postSlug;
    const blogBase =
      variant === 'inference'
        ? '/blog/the-inference'
        : variant === 'ledger'
          ? '/blog/the-ledger'
          : '/blog/article';

    return `${blogBase}/${articleSlug}`;
  },
  TERMS: '/terms',
  PRIVACY: '/privacy',
  PRODUCTS: '/products',
  ABOUT: '/about',
  OUR_STORY: '/our-story',
  SIGNUP: '/signup',
  CHINOBI_SIGNUP: '/signup/chinobi',
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  REDIRECT_HANDLER: '/redirect-handler',

  // Dashboard base (main layout)
  DASHBOARD_BASE: '/dashboard',
  DASHBOARD_VIDEO_CHAT_BASE: '/dashboard/video-chat',
  DASHBOARD_VIDEO_CHAT_CHINOBI: '/dashboard/video-chat/chinobi',

  // Dashboard main sections
  DASHBOARD_AI_AGENTS: '/dashboard/ai-agents',
  DASHBOARD_AGENT_SUITE: (suiteId: string) =>
    `/dashboard/ai-agents/suite/${suiteId}`,
  DASHBOARD_AGENT_ACTIVATION: '/dashboard/ai-agents/activate',
  DASHBOARD_AGENT_ACTIVATION_SUITE: (suiteId: string) =>
    `/dashboard/ai-agents/activate/suite/${suiteId}`,
  DASHBOARD_AGENT_ACTIVATION_AGENT: (agentId: string) =>
    `/dashboard/ai-agents/activate/agent/${agentId}`,
  DASHBOARD_AGENT_COMPANY_INFO: (suiteId: string) =>
    `/dashboard/ai-agents/suite/${suiteId}/company`,
  DASHBOARD_AGENT_DEPARTMENT_INFO: (suiteId: string) =>
    `/dashboard/ai-agents/suite/${suiteId}/department`,

  // Business Stack routes
  DASHBOARD_BUSINESS_STACK: '/dashboard/business-stack',
  DASHBOARD_BUSINESS_STACK_SELECT_AGENT:
    '/dashboard/business-stack/select-agent',
  DASHBOARD_BUSINESS_STACK_ACTIVATE_SUITE: (suiteId: string) =>
    `/dashboard/business-stack/activate-suite/${suiteId}`,
  DASHBOARD_BUSINESS_STACK_WITH_APP: (appKey: string) =>
    `/dashboard/business-stack/${appKey}`,
  DASHBOARD_BUSINESS_STACK_SALESFORCE_HELP:
    '/dashboard/business-stack/salesforce-help',

  // Knowledge Base routes
  DASHBOARD_KNOWLEDGE_BASE: '/dashboard/knowledge-base',
  DASHBOARD_KNOWLEDGE_BASE_SELECT_AGENT:
    '/dashboard/knowledge-base/select-agent',
  DASHBOARD_KNOWLEDGE_BASE_ACTIVATE_SUITE: (suiteId: string) =>
    `/dashboard/knowledge-base/activate-suite/${suiteId}`,

  // Policy Books routes
  DASHBOARD_POLICY_BOOKS: '/dashboard/policy-books',
  DASHBOARD_POLICY_BOOK_PENDING_APPROVAL:
    '/dashboard/policy-books/pending-approval',
  DASHBOARD_POLICY_BOOK_DETAILS: (suiteId: string) =>
    `/dashboard/policy-books/${suiteId}`,
  DASHBOARD_POLICY_BOOK_AUDIT_LOG: (suiteId: string) =>
    `/dashboard/policy-books/${suiteId}/audit-log`,
  DASHBOARD_POLICY_BOOK_DRAFTS: (suiteId: string) =>
    `/dashboard/policy-books/${suiteId}/drafts`,
  DASHBOARD_POLICY_BOOK_COMMENTS: (suiteId: string) =>
    `/dashboard/policy-books/${suiteId}/comments`,

  // Agentic Ticker routes
  DASHBOARD_AGENTIC_TICKER: '/dashboard/agentic-ticker',

  // Knowledge Base routes
  DASHBOARD_KNOWLEDGE_BASE_CATEGORY: (categoryId: string) =>
    `/dashboard/knowledge-base/${categoryId}`,
  DASHBOARD_KNOWLEDGE_BASE_ITEM: (categoryId: string, itemId: string) =>
    `/dashboard/knowledge-base/${categoryId}/${itemId}`,

  // Analytics section (renamed from conflicting "Dashboard")
  DASHBOARD_SELECT_AGENT: '/dashboard/analytics/select-agent',
  DASHBOARD_ANALYTICS_SUITE_DETAILS: (suiteId: string) =>
    `/dashboard/analytics/suite/${suiteId}`,
  DASHBOARD_ANALYTICS_ACTIVATE_SUITE: (suiteId: string) =>
    `/dashboard/analytics/activate-suite/${suiteId}`,

  DASHBOARD_ANALYTICS_BASE: '/dashboard/analytics',

  // New analytics structure
  DASHBOARD_ANALYTICS_INSIGHTS: '/dashboard/analytics/insights',
  DASHBOARD_ANALYTICS_INSIGHTS_ALL: '/dashboard/analytics/insights/all',
  DASHBOARD_ANALYTICS_TASK_LOGS: '/dashboard/analytics/task-logs',
  DASHBOARD_ANALYTICS_TASK_LOG_DETAILS: (taskId: string) =>
    `/dashboard/analytics/task-logs/${taskId}`,
  DASHBOARD_ANALYTICS_ASSIGNMENT_LOGS: '/dashboard/analytics/assignment-logs',
  DASHBOARD_ANALYTICS_ASSIGNMENT_LOG_DETAILS: (id: string) =>
    `/dashboard/analytics/assignment-logs/${id}`,
  DASHBOARD_ANALYTICS_SCHEDULE_HEARTBEAT:
    '/dashboard/analytics/schedule-heartbeat',

  // Settings section
  DASHBOARD_SETTINGS: '/dashboard/settings',
  DASHBOARD_SETTINGS_PROFILE: '/dashboard/settings/profile',
  DASHBOARD_SETTINGS_NOTIFICATIONS: '/dashboard/settings/notifications',
  DASHBOARD_SETTINGS_BILLING: '/dashboard/settings/billing',
  DASHBOARD_SETTINGS_MEMBERS: '/dashboard/settings/members',
  SETTINGS_CHANGE_EMAIL: '/dashboard/settings/profile/change-email',
  SETTINGS_CHANGE_PASSWORD: '/dashboard/settings/profile/change-password',

  DASHBOARD_SETTINGS_MEMBERS_INVITE: '/dashboard/settings/members/invite',

  // Members section
  DASHBOARD_MEMBERS: '/dashboard/members',
  DASHBOARD_MEMBERS_INVITE: '/dashboard/members/invite',
  DASHBOARD_MEMBERS_REQUEST_TO_JOIN: '/dashboard/members/request-to-join',
} as const;

/** Returns a route pattern relative to an optional parent route. */
export const toRoutePath = (route: string, parentRoute = '') =>
  route.slice(parentRoute.length).replace(/^\//, '');
